import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wordmark } from "@/components/common/Wordmark";
import { LoadingState, Notice, Spinner } from "@/components/common/States";
import { api, errorMessage } from "@/lib/api";
import { ROLE_LABELS, formatDate } from "@/lib/labels";

const tokenFromHash = () => new URLSearchParams(window.location.hash.slice(1)).get("t") || "";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...props} />
  </div>
);

export default function AcceptInvitePage() {
  const [token] = useState(tokenFromHash);
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm] = useState({ full_name: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadErr("Tautan undangan tidak lengkap.");
      return;
    }
    api.get("/invitations/verify", { params: { token } }).then((r) => setInfo(r.data)).catch((e) => setLoadErr(errorMessage(e)));
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!info.account_exists) {
      if (form.full_name.trim().length < 3) return setErr("Nama lengkap minimal 3 karakter.");
      if (form.password.length < 10) return setErr("Kata sandi minimal 10 karakter.");
      if (form.password !== form.confirm) return setErr("Konfirmasi kata sandi tidak sama.");
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/invitations/accept", { token, full_name: form.full_name || null, password: form.password });
      setDone(true);
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const body = () => {
    if (loadErr) return <div className="space-y-4"><Notice tone="danger" testId="invite-load-error">{loadErr}</Notice><Link to="/masuk" className="text-sm font-semibold text-[#3B82F6] hover:underline" data-testid="invite-back-login">Kembali ke halaman masuk</Link></div>;
    if (!info) return <LoadingState rows={3} />;
    if (done) {
      return (
        <div className="space-y-4 text-center" data-testid="invite-success">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden />
          <p className="font-semibold">Undangan berhasil diterima.</p>
          <Button asChild data-testid="invite-go-login"><Link to="/masuk">Masuk sekarang</Link></Button>
        </div>
      );
    }
    if (info.status !== "pending") return <div className="space-y-4"><Notice tone="danger" testId="invite-invalid">{info.message}</Notice><Link to="/masuk" className="text-sm font-semibold text-[#3B82F6] hover:underline" data-testid="invite-invalid-back-login">Kembali ke halaman masuk</Link></div>;
    return (
      <form onSubmit={submit} className="space-y-4" data-testid="invite-accept-form" noValidate>
        <div className="rounded-lg bg-muted p-4 text-sm space-y-1">
          <p><span className="text-muted-foreground">Sekolah:</span> <b data-testid="invite-school-name">{info.school_name}</b></p>
          <p><span className="text-muted-foreground">Peran:</span> <b>{ROLE_LABELS[info.role]}</b></p>
          <p><span className="text-muted-foreground">Email:</span> {info.email}</p>
          <p><span className="text-muted-foreground">Berlaku hingga:</span> {formatDate(info.expires_at)}</p>
        </div>
        {info.account_exists ? (
          <Field id="pw" label="Kata sandi akun Anda yang sudah ada" type="password" value={form.password} onChange={set("password")} data-testid="invite-password-input" />
        ) : (
          <>
            <Field id="fn" label="Nama lengkap" value={form.full_name} onChange={set("full_name")} placeholder="Nama sesuai identitas" data-testid="invite-fullname-input" />
            <Field id="pw" label="Buat kata sandi" type="password" value={form.password} onChange={set("password")} placeholder="Minimal 10 karakter, huruf dan angka" data-testid="invite-password-input" />
            <Field id="pw2" label="Ulangi kata sandi" type="password" value={form.confirm} onChange={set("confirm")} data-testid="invite-confirm-input" />
          </>
        )}
        {err && <p className="text-sm text-[#E11D2E]" role="alert" data-testid="invite-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="invite-accept-button">{busy && <Spinner />} Terima undangan</Button>
      </form>
    );
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md space-y-6 sist-rise">
        <Wordmark />
        <h1 className="font-brand text-3xl font-extrabold">Terima Undangan</h1>
        {body()}
      </div>
    </div>
  );
}
